<script setup lang="ts">
import BaseIcon from '@c/common/Icon.vue';
import type { PropType } from 'vue';

defineProps({
  /** 禁用属性 */
  disabled: {
    type: Boolean,
    default: false,
  },
  /** 按钮类型 */
  type: {
    type: String as PropType<'' | 'primary' | 'primary-light' | 'white' | 'black' | 'gray' | 'error'>,
    default: '',
  },
  /**
   * 按钮大小
   * ```ts
   * mini:            'h-32px px-14px text-14px'
   * small:           'h-36px px-14px text-14px'
   * small-large:     'h-40px px-16px text-14px'
   * medium:          'h-44px px-18px text-16px'
   * medium-large:    'h-48px px-20px text-16px'
   * large:           'h-60px px-28px text-18px'
   * ```
   */
  size: {
    type: String as PropType<'' | 'mini' | 'small' | 'small-large' | 'medium' | 'medium-large' | 'large'>,
    default: '',
  },
  /** 加载状态 */
  loading: {
    type: Boolean,
    default: false,
  },
  /** 圆角 */
  round: {
    type: Boolean,
    default: false,
  },
  /** 朴素按钮 */
  plain: {
    type: Boolean,
    default: false,
  },
});
// 尺寸映射
const sizeClsMap = {
  'mini': 'h-32px px-14px text-sm-m',
  'small': 'h-36px px-14px text-sm-m',
  'small-large': 'h-40px px-16px text-sm-m',
  'medium': 'h-44px px-18px text-md-m',
  'medium-large': 'h-48px px-20px text-md-m',
  'large': 'h-60px px-28px text-lg-m',
};
</script>

<template>
  <a
    class="ts button"
    :class="[
      type ? `button-${type}` : '',
      size && sizeClsMap[size] ? sizeClsMap[size] : '',
      round ? 'rd-100px' : 'rd-8px',
      {
        'hover:opacity-80': !plain && !type,
        'button-gray': plain && !type,
        'shadow-[0_1px_2px_0_#1018280d]': !plain && type,
        'is-loading': loading,
        'is-round': round,
        'is-plain': plain,
        'is-disabled': disabled,
      },
    ]"
  >
    <div
      v-if="disabled || loading"
      class="absolute z-10 inset-0"
      @click.stop.prevent
    />
    <BaseIcon
      v-if="loading"
      name="loading"
      class="text-18px"
    />
    <slot />
  </a>
</template>

<style lang="scss" scoped>
.button {
  @apply relative shrink-0 inline-flex flex-row items-center justify-center gap-x-8px whitespace-nowrap select-none fw-500;

  // 通用 disabled 样式
  &.is-disabled {
    @apply cursor-not-allowed overflow-hidden;
  }

  // 通用 loading 样式
  &.is-loading {
    @apply cursor-progress overflow-hidden;
  }

  // 黑色主题按钮
  &.button-black {
    &:not(.is-plain) {
      @apply bg-[--gray-900] c-[--white];

      &:not(.is-disabled):hover {
        @apply bg-[--gray-700];
      }

      &.is-disabled {
        @apply bg-[--gray-300];
      }
    }

    &.is-plain {
      @apply shadow-[0_0_0_1px_var(--gray-900)]  c-[--gray-900];

      &:not(.is-disabled):hover {
        @apply bg-[--gray-25] c-[--gray-600];
      }

      &.is-disabled {
        @apply shadow-[0_0_0_1px_var(--gray-300)] c-[--gray-300];
      }
    }
  }

  // 主题色主题按钮
  &.button-primary {
    &:not(.is-plain) {
      @apply bg-[--primary] c-[--white];

      &:not(.is-disabled):hover {
        @apply bg-[--primary-600];
      }

      &.is-disabled {
        @apply bg-[--gray-300];
      }
    }

    &.is-plain {
      @apply shadow-[0_0_0_1px_var(--primary)]  c-[--primary];

      &:not(.is-disabled):hover {
        @apply bg-[--primary-25] c-[--primary-600];
      }

      &.is-disabled {
        @apply shadow-[0_0_0_1px_var(--primary-100)] c-[--primary-100];
      }
    }
  }

  // 主题色-浅色主题按钮
  &.button-primary-light {
    &:not(.is-plain) {
      @apply bg-[--primary-50] c-[--primary];

      &:not(.is-disabled):hover {
        @apply bg-[--primary-100];
      }

      &.is-disabled {
        @apply bg-[--white] shadow-[0_0_0_1px_var(--primary-100)] c-[--primary-100];
      }
    }

    &.is-plain {
      @apply shadow-[0_0_0_1px_var(--primary-100)]  c-[--primary];

      &:not(.is-disabled):hover {
        @apply bg-[--primary-25] c-[--primary];
      }

      &.is-disabled {
        @apply shadow-[0_0_0_1px_var(--primary-100)] c-[--primary-100];
      }
    }
  }

  // 白色主题按钮
  &.button-white {
    &:not(.is-plain) {
      @apply bg-[--white] c-[--gray-700];

      &:not(.is-disabled):hover {
        @apply bg-[--gray-100];
      }

      &.is-disabled {
        @apply bg-[--gray-100];
      }
    }

    &.is-plain {
      @apply shadow-[0_0_0_1px_var(--white)]  c-[--white];

      &:not(.is-disabled):hover {
        @apply bg-[--white-10] c-[--gray-200];
      }

      &.is-disabled {
        @apply shadow-[0_0_0_1px_var(--gray-300)] c-[--gray-300];
      }
    }
  }

  // 灰色主题按钮
  &.button-gray {
    &:not(.is-plain) {
      @apply bg-[--gray-50] c-[--gray-700];

      &:not(.is-disabled):hover {
        @apply bg-[--gray-100] c-[--gray-800];
      }

      &.is-disabled {
        @apply bg-[--gray-50] c-[--gray-300];
      }
    }

    &.is-plain {
      @apply shadow-[0_0_0_1px_var(--gray-300)]  c-[--gray-700];

      &:not(.is-disabled):hover {
        @apply bg-[--gray-50] c-[--gray-800];
      }

      &.is-disabled {
        @apply shadow-[0_0_0_1px_var(--gray-300)] c-[--gray-300];
      }
    }
  }

  // 灰色主题按钮
  &.button-error {
    &:not(.is-plain) {
      @apply bg-[--error-600] c-[--white];

      &:not(.is-disabled):hover {
        @apply bg-[--error-700];
      }

      &.is-disabled {
        @apply bg-[--error-200];
      }
    }

    &.is-plain {
      @apply shadow-[0_0_0_1px_var(--error-300)]  c-[--error-700];

      &:not(.is-disabled):hover {
        @apply bg-[--error-50] c-[--error-800];
      }

      &.is-disabled {
        @apply shadow-[0_0_0_1px_var(--error-200)] c-[--error-300];
      }
    }
  }
}
</style>
